//@flow
import React from 'react';
import {
  Divider,
  makeStyles,
  Typography,
  Collapse,
  IconButton,
} from '@material-ui/core';

import {colors} from '../../../assets/styles/colors';

import {commaFormat, percentageRatio} from '../../../utils/number';
import CustomButton from '../../../elements/CustomButton/CustomButton';
import {useState} from 'react';
import DiscountForm from './DiscountForm';
import CouponForm from './CouponForm';
import TaxModal from './TaxModal';

import {Delete} from '@material-ui/icons';
import {useTypedSelector} from '../../../store';

import type {
  CartSummaryCoupon,
  Currency,
  CartSummaryGlobalDiscount,
  CartSummaryItemPromotion,
} from '../../../entities';

import type {ShoppingCart} from '../duck';
import {useStoreCurrency} from '../../currency/utils';

type Props = {
  subTotal?: number | string,
  shoppingCart: ShoppingCart,
  currency: Currency,
  coupon?: CartSummaryCoupon,
  promotionsApplied: CartSummaryItemPromotion[],
  total?: number | string,
  handleGlobalDiscount: Function,
  handleRemoveGlobalDiscount: Function,
  handleCouponApply: (coupon: string) => void,
  handleCouponRemove: () => void,
  globalDiscount?: CartSummaryGlobalDiscount,
  couponDiscount?: string,
};

const useStyles = makeStyles(theme => ({
  divider: {
    background: colors.grey200,
    width: '100%',
  },
  shoppingCartFooter: {
    marginTop: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  shoppingCartSubtotal: {
    color: colors.grey800,
  },
  textPurple: {
    color: colors.purple,
  },
  deleteBtn: {
    color: colors.error,
    padding: 0,
    marginLeft: '.5rem',
  },
  shoppingCartTotal: {
    color: colors.grey800,
    fontWeight: 600,
  },
  shoppingCartPromotionDiscount: {
    color: colors.success,
    fontSize: 14,
    whiteSpace: 'nowrap',
    maxWidth: '70%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  couponCode: {
    fontSize: 14,
    color: colors.grey500,
  },
  summaryOptions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  summaryOptionText: {
    color: colors.grey700,
    fontSize: '14px',
  },
  summaryOptionBtn: {
    backgroundColor: colors.grey100,
    borderRadius: '5px',
    color: colors.grey700,
    fontSize: '14px',
    border: `.5px solid ${colors.grey400}`,
  },
  summaryOptionBtnActive: {
    backgroundColor: '#E4D6FF',
    color: colors.purple,
    fontWeight: 600,
  },
  collapse: {
    marginTop: '8px',
  },
  editTaxBtn: {
    color: colors.purple,
    padding: 0,
  },
}));

export default function ShoppingCartFooter(props: Props) {
  const {
    subTotal = 0,
    shoppingCart,
    coupon,
    promotionsApplied,
    total,
    handleGlobalDiscount,
    handleCouponApply,
    handleCouponRemove,
    globalDiscount,
    handleRemoveGlobalDiscount,
    couponDiscount,
  } = props;

  const currency = useStoreCurrency();

  const applicableCoupons = useTypedSelector(
    state => state.manualOrder.applicableCoupons
  );
  // const scTaxes = useTypedSelector(state => state.manualOrder.taxes);
  const [taxModal, setTaxModal] = useState(false);

  /*   const openModal = () => {
    setTaxModal(true);
  }; */
  const closeModal = () => {
    setTaxModal(false);
  };

  const [discountType, setDiscountType] = useState<'coupon' | 'amount' | null>(
    null
  );

  const toggleDiscountType = (type: 'coupon' | 'amount') => {
    if (discountType === type) {
      setDiscountType(null);
      return;
    }
    setDiscountType(type);
  };

  const classes = useStyles();

  const discountPromotionValue = promotionsApplied
    ? promotionsApplied.reduce((acc, promo) => {
        return acc + Number(promo.discount);
      }, 0)
    : 0;

  const couponDiscountValue = coupon ? Number(couponDiscount) : 0;

  const maxDiscount =
    +subTotal - (discountPromotionValue + couponDiscountValue);

  let defaultGlobalDiscount;
  if (globalDiscount) {
    if (globalDiscount.description === 'FIXED_DISCOUNT') {
      defaultGlobalDiscount = globalDiscount.discount;
    }

    if (globalDiscount.description === 'PERCENTAGE_DISCOUNT') {
      const percentage = percentageRatio(total || 0, subTotal);
      const percentageNumber = Number(percentage.replace('%', ''));
      defaultGlobalDiscount = Math.abs(percentageNumber);
    }
  }

  return (
    <div className={classes.shoppingCartFooter}>
      <Divider className={classes.divider} />

      <div className={classes.summaryOptionsContainer}>
        <div className={classes.summaryOptions}>
          <Typography className={classes.summaryOptionText}>Añadir</Typography>
          <CustomButton
            size="small"
            onClick={() => toggleDiscountType('amount')}
            className={`${classes.summaryOptionBtn} ${
              discountType === 'amount' ? classes.summaryOptionBtnActive : ''
            }`}
          >
            Descuento
          </CustomButton>
          <CustomButton
            size="small"
            onClick={() => toggleDiscountType('coupon')}
            className={`${classes.summaryOptionBtn} ${
              discountType === 'coupon' ? classes.summaryOptionBtnActive : ''
            }`}
          >
            Cupón
          </CustomButton>
        </div>
        <Collapse className={classes.collapse} in={!!discountType}>
          {discountType === 'amount' ? (
            <DiscountForm
              defaultDiscount={defaultGlobalDiscount}
              defaultType={
                globalDiscount &&
                globalDiscount.description === 'PERCENTAGE_DISCOUNT'
                  ? 'percentage'
                  : 'amount'
              }
              maxPrice={maxDiscount}
              onApplyDiscount={handleGlobalDiscount}
            />
          ) : (
            <CouponForm
              promotionApplied={!!(promotionsApplied?.length > 0)}
              applicableCoupons={applicableCoupons}
              currentCoupon={coupon && coupon.code}
              onRemoveCoupon={handleCouponRemove}
              onApplyCoupon={handleCouponApply}
            />
          )}
        </Collapse>
      </div>

      <div className={classes.row}>
        <Typography className={classes.shoppingCartSubtotal}>
          Subtotal
        </Typography>
        <Typography className={classes.shoppingCartSubtotal}>
          {subTotal && shoppingCart.items.length
            ? commaFormat(subTotal, currency.symbol)
            : '--'}
        </Typography>
      </div>
      {coupon && coupon.code && couponDiscount ? (
        <div className={classes.row}>
          <Typography className={classes.textPurple}>
            Cupón <span className={classes.couponCode}>{coupon.code}</span>
          </Typography>
          <Typography className={classes.shoppingCartPromotionDiscount}>
            -{commaFormat(couponDiscount, currency.symbol)}
          </Typography>
        </div>
      ) : null}
      {promotionsApplied?.map(p => (
        <div className={classes.row} key={`promo-summary-${p.name}`}>
          <Typography className={classes.shoppingCartPromotionDiscount}>
            {p.name}
          </Typography>
          <Typography className={classes.shoppingCartPromotionDiscount}>
            -{commaFormat(p.discount, currency.symbol)}
          </Typography>
        </div>
      ))}
      {globalDiscount && (
        <div className={classes.row}>
          <Typography className={classes.textPurple}>
            Descuento
            <IconButton
              className={classes.deleteBtn}
              onClick={() => handleRemoveGlobalDiscount()}
            >
              <Delete />
            </IconButton>
          </Typography>
          <Typography className={classes.shoppingCartSubtotal}>
            -{commaFormat(globalDiscount.discount, currency.symbol)}
          </Typography>
        </div>
      )}

      <div className={classes.row}>
        <Typography className={classes.shoppingCartTotal}>Total</Typography>
        <Typography className={classes.shoppingCartTotal}>
          {total && shoppingCart.items.length
            ? commaFormat(total, currency.symbol)
            : '--'}
        </Typography>
      </div>
      <TaxModal open={taxModal} onClose={closeModal} />
    </div>
  );
}
